/*
  YNV CONFIGURATOR ENGINE V17 - DYNAMIC HERO IMAGE
  - Clicking an occasion / palette / style / format card swaps the main product image (config "image" per entry)
  - V16 base: extras + notes + money format
  - Adds Extras step (optional add-ons via checkboxes, recorded as line-item properties)
  - Adds Notes step (optional 500-char note for the florist, recorded as line-item property)
  - Flow: Occasion → Palette → Style → Format → Extras → Notes → Investment
  - money() now respects the shop money format injected by the template (YNV_MONEY_FORMAT)
  - V15 base: fully synchronized with Theme Settings fonts/colours
*/

async function loadYNVConfig() {
  const url = window.YNV_CONFIG_URL;
  if (!url) throw new Error('YNV_CONFIG_URL not set');
  const res = await fetch(url, { cache: 'no-store' });
  if (!res.ok) throw new Error('Could not load ynv-config.json');
  return res.json();
}

/**
 * @param {string} tag
 * @param {Object} attrs
 * @param {string} html
 */
function el(tag, attrs = {}, html = '') {
  const node = document.createElement(tag);
  Object.entries(attrs).forEach(([k, v]) => {
    if (k === 'class') node.className = v;
    else if (k === 'dataset' && typeof v === 'object') Object.assign(node.dataset, v);
    else if (v === null || v === undefined) return;
    else node.setAttribute(k, v);
  });
  if (html) node.innerHTML = html;
  return node;
}

function injectStyles() {
  if (document.getElementById('ynv-configurator-styles-v16')) return;

  const style = el('style', { id: 'ynv-configurator-styles-v16' }, `
    /* Product media stays visible on the left (theme's native two-column
       layout); the configurator lives in the details column on the right. */

    /* --- CONFIGURATOR STYLES --- */
    .ynv-wrap{ width:100%; max-width:100%; margin:0; padding:10px 0; position: relative; z-index: 2; }
    
    .ynv-stepper{ margin:0 0 20px; text-align: left; display:flex; align-items:center; gap:14px; }
    /* Mobile-only thumbnail mirroring the hero image (hero sits above the steps on small screens) */
    .ynv-step-thumb{ display:none; width:64px; height:80px; object-fit:cover; border-radius:4px; flex:0 0 auto; background:#f2efe9; transition: opacity 180ms ease-out; }
    @media(max-width: 749px){ .ynv-step-thumb{ display:block; } }
    
    /* Updated Header Font to Match Theme Exactly */
    .ynv-stepper h2 { 
      margin: 0; 
      font-family: var(--font-heading--family) !important; 
      font-style: var(--font-heading--style) !important;
      font-weight: var(--font-heading--weight) !important;
      font-size: 1.8rem; 
      color: rgb(var(--color-foreground-rgb));
      text-transform: uppercase; /* Match theme headers */
      letter-spacing: 0.1em; /* Match theme spacing */
    }
    
    .ynv-sub{ 
      opacity:.75; 
      font-size:0.95rem; 
      margin-top:5px; 
      font-family: var(--font-body--family) !important; 
      letter-spacing: 0.02em; 
      color: rgb(var(--color-foreground-rgb));
    }
    
    .ynv-layout{ display:block; width: 100%; }

    .ynv-grid{ display:grid; grid-template-columns:repeat(2, 1fr); gap:10px; }
    @media(min-width: 768px) {
      .ynv-grid{ grid-template-columns:repeat(3, 1fr); }
    }

    .ynv-card { 
      border: 1px solid rgba(var(--color-foreground-rgb), 0.15) !important;
      background: rgba(255,255,255,0.4) !important; 
      width: webkit-fill-available; 
      width: -moz-available;
      width: 100%;
      border-radius: 2px !important; 
      padding: 14px 10px !important;
      cursor: pointer; 
      color: rgb(var(--color-foreground-rgb));
      text-align: center;
      display: flex; 
      flex-direction: column; 
      align-items: center;
      justify-content: center; 
      gap: 4px; 
      transition: all 0.3s ease;
      min-height: 50px !important;
    }
    
    /* Button Title Font - Capitalized and spaced like theme buttons */
    .ynv-card-title { 
      font-family: var(--font-heading--family) !important; /* Use heading font for stronger presence */
      font-weight: 500; 
      font-size: 0.8rem !important; 
      text-transform: uppercase; 
      letter-spacing: 0.15em; 
    }
    
    .ynv-note { 
      font-size:.7rem !important; 
      font-style: italic; 
      opacity: 0.7; 
      font-family: var(--font-body--family) !important; 
    }

    .ynv-card:hover { 
      border-color: rgba(var(--color-foreground-rgb), 0.5) !important; 
      background: rgba(255,255,255,0.8) !important;
    }
    
    /* Active State - Matches Theme Primary Button Color */
    .ynv-card:focus, .ynv-card[aria-selected="true"] { 
      border-color: rgb(var(--color-foreground-rgb)) !important; 
      background-color: rgb(var(--color-foreground-rgb)) !important; 
      color: rgb(var(--color-background-rgb)) !important; 
    }
    
    .ynv-card[aria-selected="true"] .ynv-note { opacity: 0.8; font-weight: 300; }
    
    .ynv-actions{ display:flex; gap:12px; justify-content:flex-start; margin-top:25px; padding-top: 15px; border-top: 1px solid rgba(0,0,0,0.05); }
    
    .ynv-btn { 
      border-radius: 2px !important; 
      padding: 12px 30px !important; 
      border: 1px solid rgb(var(--color-foreground-rgb)) !important; 
      background: transparent !important; 
      color: rgb(var(--color-foreground-rgb)) !important;
      cursor: pointer; 
      font-weight: 600; 
      font-family: var(--font-body--family) !important; 
      font-size: 0.75rem !important; 
      text-transform: uppercase; 
      letter-spacing: 0.15em; 
      transition: all 0.3s ease;
    }
    
    .ynv-btn:hover {
      background: rgba(var(--color-foreground-rgb), 0.05) !important;
    }
    
    .ynv-btn-primary { 
      background: rgb(var(--color-foreground-rgb)) !important; 
      color: rgb(var(--color-background-rgb)) !important; 
    }
    
    .ynv-btn-primary:hover {
      opacity: 0.9;
    }
    
    .ynv-btn[disabled]{ opacity:.4; cursor:not-allowed; border-color: rgba(0,0,0,0.2) !important; background: transparent !important; color: rgba(0,0,0,0.5) !important; }

    /* --- EXTRAS STEP --- */
    .ynv-extra-card { cursor: pointer; }
    .ynv-extra-checkbox {
      width: 16px; height: 16px; margin: 0 0 4px;
      accent-color: rgb(var(--color-foreground-rgb));
      cursor: pointer;
    }
    .ynv-card[aria-selected="true"] .ynv-extra-checkbox { accent-color: rgb(var(--color-background-rgb)); }
    .ynv-extra-price {
      font-family: var(--font-body--family) !important;
      font-size: 0.75rem !important; font-weight: 600;
      letter-spacing: 0.08em; margin-top: 2px;
    }

    /* --- NOTES STEP --- */
    .ynv-notes-box { width: 100%; }
    .ynv-textarea {
      width: 100%; box-sizing: border-box; resize: vertical; min-height: 120px;
      border: 1px solid rgba(var(--color-foreground-rgb), 0.15);
      border-radius: 2px; background: rgba(255,255,255,0.4);
      color: rgb(var(--color-foreground-rgb));
      font-family: var(--font-body--family) !important;
      font-size: 0.9rem; line-height: 1.6; padding: 12px 14px;
      transition: border-color 0.3s ease;
    }
    .ynv-textarea:focus {
      outline: none;
      border-color: rgb(var(--color-foreground-rgb));
      background: rgba(255,255,255,0.8);
    }
    .ynv-char-counter {
      font-family: var(--font-body--family) !important;
      font-size: 0.7rem; font-style: italic; opacity: 0.7;
      margin-top: 6px; text-align: right;
      color: rgb(var(--color-foreground-rgb));
    }
    .ynv-palette-guide-hint {
      text-align: center;
      margin-top: 16px;
      margin-bottom: 8px;
      font-family: 'Work Sans', sans-serif;
      font-size: 13px;
      color: #333333;
    }
    .ynv-palette-guide-hint a {
      color: #A98D6A;
      text-decoration: none;
      font-weight: 500;
      margin-left: 4px;
    }
    .ynv-palette-guide-hint a:hover {
      color: #333333;
      text-decoration: underline;
    }
  `);
  document.head.appendChild(style);
}

const norm = (v) => (v ?? '').toString().trim().toLowerCase();

function getDisabledIds(key) {
  const raw = window.YNV_PRODUCT?.metafields?.ynv?.[key];
  if (raw == null) return [];
  if (Array.isArray(raw)) return raw.map(norm).filter(Boolean);
  const s = norm(raw);
  return s.split(/[,•|]+/g).map(x => x.trim()).filter(Boolean);
}

function getQueryParams() {
  const qs = new URLSearchParams(window.location.search);
  const out = {};
  for (const [k, v] of qs.entries()) out[norm(k)] = (v || '').toString();
  return out;
}

const isEnabled = (obj) => obj?.enabled !== false;
const byId = (list) => Object.fromEntries((list || []).map(x => [x.id, x]));

function money(cents) {
  const value = (Number(cents) || 0) / 100;
  const fmt = window.YNV_MONEY_FORMAT;
  if (typeof fmt === 'string' && fmt.indexOf('{{') !== -1) {
    const thousands = (s) => s.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const amount = thousands(value.toFixed(2)).replace(/\.00$/, '');
    const noDecimals = thousands(String(Math.round(value)));
    const euroAmount = value.toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return fmt
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, String(Math.round(value)).replace(/\B(?=(\d{3})+(?!\d))/g, '.'))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, euroAmount)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDecimals)
      .replace(/\{\{\s*amount\s*\}\}/g, amount);
  }
  return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD', maximumFractionDigits: 0 }).format(value);
}

/* --- HERO IMAGE SWAP (V17) ---
   Each occasion / palette / style / format entry in ynv-config.json may carry an
   "image" (a theme asset filename). Clicking a card swaps the product's main
   media image to that asset so the hero picture follows the customer's choices. */
function ynvAssetUrl(file) {
  const base = window.YNV_CONFIG_URL || '';
  if (!file || !base) return null;
  return base.replace(/ynv-config\.json.*$/, file);
}
function ynvHeroImgs() {
  // The theme renders the media gallery twice (slideshow for mobile, grid for desktop);
  // take the first media image inside each container so both stay in sync.
  const out = [];
  document.querySelectorAll('media-gallery slideshow-slides, media-gallery .media-gallery__grid').forEach(c => {
    const first = c.querySelector('.product-media-container img.product-media__image');
    if (first) out.push(first);
  });
  if (!out.length) {
    const f = document.querySelector('media-gallery img.product-media__image, .product-information__media img');
    if (f) out.push(f);
  }
  return out;
}
let ynvCurrentHero = null;
function setHeroImage(file) {
  const url = ynvAssetUrl(file);
  const imgs = ynvHeroImgs().filter(img => img.dataset.ynvHero !== url);
  if (!url || !imgs.length) return;
  const pre = new Image();
  pre.onload = () => {
    ynvCurrentHero = url;
    document.querySelectorAll('.ynv-step-thumb').forEach(t => {
      t.style.opacity = '0';
      requestAnimationFrame(() => { t.src = url; requestAnimationFrame(() => { t.style.opacity = '1'; }); });
    });
    imgs.forEach(img => {
      const pic = img.closest('picture');
      if (pic) pic.querySelectorAll('source').forEach(s => s.remove());
      if (!img.style.transition) img.style.transition = 'opacity 180ms ease-out';
      img.style.opacity = '0';
      requestAnimationFrame(() => {
        img.removeAttribute('srcset');
        img.removeAttribute('sizes');
        img.src = url;
        img.dataset.ynvHero = url;
        requestAnimationFrame(() => { img.style.opacity = '1'; });
      });
    });
  };
  pre.src = url;
}

function resetAfter(state, key) {
  const order = ['occasion', 'palette', 'style', 'format', 'extras', 'notes', 'investment'];
  const idx = order.indexOf(key);
  if (idx < 0) return;
  for (let i = idx + 1; i < order.length; i++) {
    if (order[i] === 'extras') state.extras = [];
    else if (order[i] === 'notes') state.notes = '';
    else state[order[i]] = null;
  }
}

function mountStepShell(stepMount, title, sub) {
  stepMount.innerHTML = '';
  const wrap = el('div', { class: 'ynv-wrap' });
  const stepper = el('div', { class: 'ynv-stepper' });
  const heroEl = ynvHeroImgs()[0];
  const thumbSrc = ynvCurrentHero || (heroEl && (heroEl.currentSrc || heroEl.src)) || '';
  if (thumbSrc) stepper.appendChild(el('img', { class: 'ynv-step-thumb', src: thumbSrc, alt: '' }));
  stepper.appendChild(el('div', {}, `<h2>${title}</h2><div class="ynv-sub">${sub}</div>`));
  wrap.appendChild(stepper);
  return { wrap };
}

/* --- STEPS --- */

function renderOccasion(stepMount, config, state, go) {
  const { wrap } = mountStepShell(stepMount, 'Choose an Occasion', 'We’ll guide the design based on your occasion.');
  const grid = el('div', { class: 'ynv-grid' });
  const disabled = getDisabledIds('disable_occasions');
  (config.occasions || []).filter(isEnabled).filter(o => !disabled.includes(o.id)).forEach(o => {
    const card = el('button', { class: 'ynv-card', type: 'button', 'aria-selected': state.occasion?.id === o.id ? 'true' : 'false' });
    card.appendChild(el('div', { class: 'ynv-card-title' }, o.label));
    card.addEventListener('click', () => {
      state.occasion = { id: o.id, label: o.label };
      setHeroImage(o.image);
      resetAfter(state, 'occasion');
      renderOccasion(stepMount, config, state, go);
    });
    grid.appendChild(card);
  });
  wrap.appendChild(grid);
  const actions = el('div', { class: 'ynv-actions' });
  const next = el('button', { class: 'ynv-btn ynv-btn-primary', type: 'button', disabled: state.occasion ? null : 'true' }, 'Next');
  next.addEventListener('click', () => go('palette'));
  actions.appendChild(next);
  wrap.appendChild(actions);
  stepMount.appendChild(wrap);
}

function renderPalette(stepMount, config, state, go) {
  const { wrap } = mountStepShell(stepMount, 'Choose a Palette', 'Exact flower varieties may vary.');
  // All palettes are available for every occasion — Vasthy designs palette-first,
  // so no occasion-to-palette filtering is applied.
  const disabled = getDisabledIds('disable_palettes');
  const grid = el('div', { class: 'ynv-grid' });
  (config.palettes || []).filter(p => p && isEnabled(p) && !disabled.includes(p.id)).forEach(p => {
    const card = el('button', { class: 'ynv-card', type: 'button', 'aria-selected': state.palette?.id === p.id ? 'true' : 'false' });
    card.appendChild(el('div', { class: 'ynv-card-title' }, p.label));
    if (p.mood) card.appendChild(el('div', { class: 'ynv-note' }, p.mood));
    card.addEventListener('click', () => {
      state.palette = { id: p.id, label: p.label };
      setHeroImage(p.image);
      resetAfter(state, 'palette');
      renderPalette(stepMount, config, state, go);
    });
    grid.appendChild(card);
  });
  wrap.appendChild(grid);
  const guideHint = el('div', { class: 'ynv-palette-guide-hint' },
    '<span>Not sure which palette suits the season?</span>' +
    '<a href="/pages/seasonal-bloom-guide" target="_blank" rel="noopener">See what\'s blooming now →</a>');
  wrap.appendChild(guideHint);
  const actions = el('div', { class: 'ynv-actions' });
  const back = el('button', { class: 'ynv-btn', type: 'button' }, 'Back');
  back.addEventListener('click', () => go('occasion'));
  const next = el('button', { class: 'ynv-btn ynv-btn-primary', type: 'button', disabled: state.palette ? null : 'true' }, 'Next');
  next.addEventListener('click', () => go('style'));
  actions.appendChild(back);
  actions.appendChild(next);
  wrap.appendChild(actions);
  stepMount.appendChild(wrap);
}

function renderStyle(stepMount, config, state, go) {
  const { wrap } = mountStepShell(stepMount, 'Choose a Style', 'Premium styles use premium investment levels.');
  const grid = el('div', { class: 'ynv-grid' });
  (config.styles || []).filter(isEnabled).filter(s => !getDisabledIds('disable_styles').includes(s.id)).forEach(s => {
    const card = el('button', { class: 'ynv-card', type: 'button', 'aria-selected': state.style?.id === s.id ? 'true' : 'false' });
    card.appendChild(el('div', { class: 'ynv-card-title' }, s.label));
    if (s.note) card.appendChild(el('div', { class: 'ynv-note' }, s.note));
    card.addEventListener('click', () => {
      state.style = { id: s.id, label: s.label, type: s.type || 'standard' };
      setHeroImage(s.image);
      resetAfter(state, 'style');
      renderStyle(stepMount, config, state, go);
    });
    grid.appendChild(card);
  });
  wrap.appendChild(grid);
  const actions = el('div', { class: 'ynv-actions' });
  const back = el('button', { class: 'ynv-btn', type: 'button' }, 'Back');
  back.addEventListener('click', () => go('palette'));
  const next = el('button', { class: 'ynv-btn ynv-btn-primary', type: 'button', disabled: state.style ? null : 'true' }, 'Next');
  next.addEventListener('click', () => go('format'));
  actions.appendChild(back);
  actions.appendChild(next);
  wrap.appendChild(actions);
  stepMount.appendChild(wrap);
}

function renderFormat(stepMount, config, state, go) {
  const { wrap } = mountStepShell(stepMount, 'Choose a Format', 'Hand-tied or Vase Arrangement.');
  const grid = el('div', { class: 'ynv-grid' });
  const disabled = getDisabledIds('disable_formats');
  (config.formats || []).filter(isEnabled).filter(f => !disabled.includes(norm(f.id)) && !disabled.includes(norm(f.label))).forEach(f => {
    const card = el('button', { class: 'ynv-card', type: 'button', 'aria-selected': state.format?.id === f.id ? 'true' : 'false' });
    card.appendChild(el('div', { class: 'ynv-card-title' }, f.label));
    card.addEventListener('click', () => {
      state.format = { id: f.id, label: f.label };
      setHeroImage(f.image);
      resetAfter(state, 'format');
      renderFormat(stepMount, config, state, go);
    });
    grid.appendChild(card);
  });
  wrap.appendChild(grid);
  const actions = el('div', { class: 'ynv-actions' });
  const back = el('button', { class: 'ynv-btn', type: 'button' }, 'Back');
  back.addEventListener('click', () => go('style'));
  const next = el('button', { class: 'ynv-btn ynv-btn-primary', type: 'button', disabled: state.format ? null : 'true' }, 'Next');
  next.addEventListener('click', () => go('extras'));
  actions.appendChild(back);
  actions.appendChild(next);
  wrap.appendChild(actions);
  stepMount.appendChild(wrap);
}

function renderExtras(stepMount, config, state, go) {
  const { wrap } = mountStepShell(stepMount, 'Choose Your Extras', 'Optional finishing touches — select as many as you like, or continue without any.');
  if (!Array.isArray(state.extras)) state.extras = [];
  const grid = el('div', { class: 'ynv-grid' });
  (config.extras || []).filter(isEnabled).forEach(x => {
    const selected = state.extras.some(s => s.id === x.id);
    const card = el('label', { class: 'ynv-card ynv-extra-card', 'aria-selected': selected ? 'true' : 'false' });
    const cb = el('input', { type: 'checkbox', class: 'ynv-extra-checkbox' });
    cb.checked = selected;
    card.appendChild(cb);
    card.appendChild(el('div', { class: 'ynv-card-title' }, x.label));
    if (x.description) card.appendChild(el('div', { class: 'ynv-note' }, x.description));
    card.appendChild(el('div', { class: 'ynv-extra-price' }, '+ ' + money(x.price)));
    cb.addEventListener('change', () => {
      if (cb.checked) {
        if (!state.extras.some(s => s.id === x.id)) state.extras.push({ id: x.id, label: x.label, price: x.price, variant_id: x.variant_id });
      } else {
        state.extras = state.extras.filter(s => s.id !== x.id);
      }
      renderExtras(stepMount, config, state, go);
    });
    grid.appendChild(card);
  });
  wrap.appendChild(grid);
  const actions = el('div', { class: 'ynv-actions' });
  const back = el('button', { class: 'ynv-btn', type: 'button' }, 'Back');
  back.addEventListener('click', () => go('format'));
  const next = el('button', { class: 'ynv-btn ynv-btn-primary', type: 'button' }, 'Next');
  next.addEventListener('click', () => go('notes'));
  actions.appendChild(back);
  actions.appendChild(next);
  wrap.appendChild(actions);
  stepMount.appendChild(wrap);
}

function renderNotes(stepMount, config, state, go) {
  const { wrap } = mountStepShell(stepMount, 'Add a Note', 'Any special requests or notes for your florist?');
  const box = el('div', { class: 'ynv-notes-box' });
  const ta = el('textarea', {
    class: 'ynv-textarea',
    maxlength: '500',
    rows: '5',
    placeholder: 'e.g. allergies, colour preferences, a message to include, recipient name...'
  });
  ta.value = state.notes || '';
  const counter = el('div', { class: 'ynv-char-counter' }, `${(state.notes || '').length} / 500 characters`);
  ta.addEventListener('input', () => {
    state.notes = ta.value.slice(0, 500);
    counter.textContent = `${state.notes.length} / 500 characters`;
  });
  box.appendChild(ta);
  box.appendChild(counter);
  wrap.appendChild(box);
  const actions = el('div', { class: 'ynv-actions' });
  const back = el('button', { class: 'ynv-btn', type: 'button' }, 'Back');
  back.addEventListener('click', () => go('extras'));
  const next = el('button', { class: 'ynv-btn ynv-btn-primary', type: 'button' }, 'Next');
  next.addEventListener('click', () => go('investment'));
  actions.appendChild(back);
  actions.appendChild(next);
  wrap.appendChild(actions);
  stepMount.appendChild(wrap);
}

function renderInvestment(stepMount, config, state, go) {
  const { wrap } = mountStepShell(stepMount, 'Choose Your Investment', 'Select the investment level.');
  const isPremium = state.style?.type === 'premium';
  const prefix = isPremium ? 'PR-' : (state.format?.id === 'vase' ? 'VA-' : 'HT-');
  const product = window.YNV_PRODUCT || {};
  const variants = Array.isArray(product.variants) ? product.variants : [];
  const tierVariants = variants.filter(v => (v?.sku || '').toUpperCase().startsWith(prefix) && v.available !== false).sort((a, b) => (a.price || 0) - (b.price || 0));
  const grid = el('div', { class: 'ynv-grid' });
  tierVariants.forEach(v => {
    const card = el('button', { class: 'ynv-card', type: 'button', 'aria-selected': state.investment?.variantId === v.id ? 'true' : 'false' });
    card.appendChild(el('div', { class: 'ynv-card-title' }, money(v.price)));
    card.appendChild(el('div', { class: 'ynv-note' }, isPremium ? 'Premium level' : 'Market fresh'));
    card.addEventListener('click', () => {
      state.investment = { amount: v.price, variantId: v.id, sku: v.sku || '' };
      renderInvestment(stepMount, config, state, go);
    });
    grid.appendChild(card);
  });
  wrap.appendChild(grid);
  const actions = el('div', { class: 'ynv-actions' });
  const back = el('button', { class: 'ynv-btn', type: 'button' }, 'Back');
  back.addEventListener('click', () => go('notes'));
  const buy = el('button', { class: 'ynv-btn ynv-btn-primary', type: 'button', disabled: state.investment ? null : 'true' }, 'Add to cart');
  buy.addEventListener('click', async () => {
    const variantId = state.investment?.variantId || window.YNV_SELECTED_OR_FIRST_VARIANT_ID;
    if (!variantId) return alert('Error: Missing variant ID.');
    buy.disabled = true; buy.textContent = 'Adding…';
    try {
      const properties = { "Occasion": state.occasion?.label, "Palette": state.palette?.label, "Style": state.style?.label, "Format": state.format?.label };
      const note = (state.notes || '').trim();
      if (note) properties["Notes"] = note.slice(0, 500);

      // Selected extras become separate priced line items (real addon products).
      // The parent/addon _ynv_* grouping matches ynv-addons.js conventions: the
      // orphan sweeper keeps the extras alive while the arrangement is in the
      // cart and removes them automatically if the arrangement is removed. A
      // unique group id per submission keeps multiple arrangements independent.
      const selectedExtras = (state.extras || []).filter(x => x.variant_id);
      if (selectedExtras.length) {
        const groupId = 'ynv_cfg_' + Date.now() + '_' + Math.random().toString(16).slice(2);
        properties["_ynv_group"] = groupId;
        properties["_ynv_role"] = 'parent';
        var extraItems = selectedExtras.map(x => ({
          id: Number(x.variant_id),
          quantity: 1,
          properties: { "_ynv_group": groupId, "_ynv_role": 'addon' }
        }));
      } else {
        var extraItems = [];
      }

      const items = [{ id: Number(variantId), quantity: 1, properties }, ...extraItems];
      const res = await fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items }) });
      if (!res.ok) throw new Error('Failed');
      window.location.href = '/cart';
    } catch {
      alert('Could not add to cart.'); buy.disabled = false; buy.textContent = 'Add to cart';
    }
  });
  actions.appendChild(back);
  actions.appendChild(buy);
  wrap.appendChild(actions);
  stepMount.appendChild(wrap);
}

/* --- INIT --- */
document.addEventListener('DOMContentLoaded', async () => {
  const mount = document.getElementById('ynv-configurator');
  if (!mount) return;
  injectStyles();
  mount.innerHTML = '';
  
  const stepsCol = el('div', { id: 'ynv-steps', class: 'ynv-main-container' });
  mount.appendChild(stepsCol);

  const state = { occasion: null, palette: null, style: null, format: null, extras: [], notes: '', investment: null };

  try {
    const config = await loadYNVConfig();
    const go = (id) => {
      if (id === 'occasion') renderOccasion(stepsCol, config, state, go);
      else if (id === 'palette') renderPalette(stepsCol, config, state, go);
      else if (id === 'style') renderStyle(stepsCol, config, state, go);
      else if (id === 'format') renderFormat(stepsCol, config, state, go);
      else if (id === 'extras') renderExtras(stepsCol, config, state, go);
      else if (id === 'notes') renderNotes(stepsCol, config, state, go);
      else if (id === 'investment') renderInvestment(stepsCol, config, state, go);
    };

    // Pre-select the occasion when arriving from an occasion page via ?occasion=<label>.
    // Case-insensitive, punctuation-insensitive match on label or id, with prefix
    // tolerance so "Sympathy" matches "Sympathy Flowers". On a match the flow opens
    // on the Palette step; Back still returns to the Occasion step to change it.
    let startStep = 'occasion';
    const occasionParam = getQueryParams()['occasion'] || '';
    if (occasionParam) {
      const normKey = (s) => norm(s).replace(/[^a-z0-9]/g, '');
      const want = normKey(occasionParam);
      const enabledOccasions = (config.occasions || []).filter(isEnabled);
      const match = want && (
        enabledOccasions.find(o => normKey(o.label) === want || normKey(o.id) === want) ||
        enabledOccasions.find(o => normKey(o.label).startsWith(want) || want.startsWith(normKey(o.label)))
      );
      if (match) {
        state.occasion = { id: match.id, label: match.label };
        setHeroImage(match.image);
        startStep = 'palette';
      }
    }
    go(startStep);
  } catch (e) { console.error(e); }
});